import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useCallback } from 'react';
import cx from 'clsx';

import { WiderTable, SortOrder } from '@/components';
import { openNewWindow, useCurrentUserId } from '@/utils';

import { getColumns } from './columns';
import { useBadgeMintUsers, useMinterFollower } from '../../hooks';
import style from './index.module.scss';

const PAGE_SIZE = 100;

export const MintRecords = ({ id, className }: { id: string; className?: string }) => {
  const { t } = useTranslation();

  const currentAddress = useCurrentUserId();
  const [start, setStart] = useState(0);
  const [orderBy, setOrderBy] = useState<string>();
  const { data, isLoading, refetch, isError } = useBadgeMintUsers(id, start, orderBy);
  const setMinterFollower = useMinterFollower();

  const columns = useMemo(
    () => getColumns(setMinterFollower, currentAddress, refetch),
    [refetch, currentAddress, setMinterFollower],
  );

  const pagination = useMemo(
    () => ({
      current: Math.floor(start / PAGE_SIZE) + 1,
      defaultPageSize: PAGE_SIZE,
      onChange: (page: number) => {
        setStart(PAGE_SIZE * (page - 1));
      },
      total: data?.total_count,
      showQuickJumper: true,
      showSizeChanger: false,
    }),
    [data?.total_count, start, setStart],
  );

  const onRowClick = useCallback(row => {
    const id = row?.user?.id;
    if (id) {
      openNewWindow(`/profile/${id}`, '_blank');
    }
  }, []);

  const onSort = useCallback(
    ([[key, sort]]: Array<[string, SortOrder]>) => {
      setOrderBy(key ? `${sort === 'desc' ? '-' : ''}${key}` : undefined);
      setStart(0);
    },
    [setOrderBy, setStart],
  );

  return (
    <div className={cx(style.container, className)}>
      <div className={style.tableHeader}>
        {data?._totalCount} {t('MintRecords.phkx83')}
      </div>
      <WiderTable
        onRowClick={onRowClick}
        orderBy={orderBy}
        columns={columns}
        data={data?.mint_list || []}
        reload={refetch}
        loading={isLoading}
        isError={isError}
        pagination={pagination}
        skeleton
        className={style.table}
        onSort={onSort}
      />
    </div>
  );
};
